import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback
} from 'react';
import ImageGallery from 'react-image-gallery';
import types from 'prop-types';
import { insertCss } from '@rexlabs/styling';
import { Box } from '@rexlabs/box';

import data from 'data/listing';
import ListingModal from '../listing-modal/ListingModal';

import FullScreenSVG from './svgs/fullscreen.svg';
import './styles.css';

insertCss(`
  .image-gallery-fullscreen-button::before { display:none!important }

  .image-gallery-svg {
    display: none !important;
  }

  .image-gallery-fullscreen-button {
    top:0;
    margin: 10px;
    padding: 10px;
    height: 40px;
    background-color: rgba(23,23,23,0.5) !important;
    border-radius: 2px;
  }

  .image-gallery-slide .image-gallery-description {
     bottom: 0 !important;
     width: 100%;
   }
`);

function mapImage(image) {
  return {
    original: image.full,
    thumbnail: image.thumb || image.full
  };
}

const FullScreenIcon = (onClick, isFullscreen) => {
  return (
    <div
      className={`image-gallery-fullscreen-button${
        isFullscreen ? ' active' : ''
      }`}
    >
      <FullScreenSVG onClick={onClick} />
    </div>
  );
};

function ListingGallery(props) {
  const {
    theme,
    thumbLocation,
    floorplans,
    images: listingImages,
    renderModal,
    video,
    map,
    virtualTour,
    startIndex
  } = props;

  // Init State
  const [modalOpen, setModalOpen] = useState(false);
  const [screen, setScreen] = useState('images');
  const [mounted, setMounted] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const galleryRef = useRef(null);

  const images = useMemo(() => listingImages?.map(mapImage) ?? [], [
    listingImages
  ]);

  const openScreen = useCallback(
    (screen) => {
      if (mounted) {
        setModalOpen(true);
        setScreen(screen);
      }
    },
    [mounted]
  );

  // On Load
  useEffect(() => {
    // Setup listeners for the clicks
    const event = document.createEvent('Event');
    event.initEvent('media-click', true, true);

    const open = (e) => openScreen(e.target.dataset.screen);
    document.addEventListener('media-click', open);

    setMounted(true);

    // Clean everything up
    return () => {
      document.removeEventListener('media-click', open);
      setMounted(false);
    };
  }, [openScreen]);

  function openModal() {
    setModalOpen(true);
  }

  function closeModal() {
    setModalOpen(false);
  }

  function updateIndex(i) {
    setCurrentIndex(i);
  }

  const sharedProps = {
    items: images,
    showPlayButton: false,
    thumbnailPosition: thumbLocation,
    showNav: true,
    showIndex: true,
    startIndex: startIndex ?? 0,
    ref: galleryRef,
    useBrowserFullscreen: false,
    onSlide: updateIndex
  };

  return (
    <Box width={'100%'}>
      <Box className={'desktop-gallery'}>
        <ImageGallery
          {...sharedProps}
          lazyLoad={true}
          renderFullscreenButton={FullScreenIcon}
          onClick={openModal}
        />
      </Box>

      <Box className={'mobile-gallery'}>
        <ImageGallery {...sharedProps} onClick={openModal} />
      </Box>

      {modalOpen && renderModal && (
        <ListingModal
          theme={theme}
          closeModal={closeModal}
          images={listingImages}
          floorplans={floorplans}
          screen={screen}
          video={video}
          virtualTour={virtualTour}
          startIndex={currentIndex}
          {...map}
        />
      )}
    </Box>
  );
}

ListingGallery.propTypes = {
  renderModal: types.bool,
  thumbLocation: types.string,
  images: types.array,
  floorplans: types.array,
  video: types.string,
  map: types.object,
  virtualTour: types.string,
  startIndex: types.number
};

ListingGallery.defaultProps = {
  renderModal: false,
  thumbLocation: 'right',
  images: data.images,
  map: {
    latitude: -27.452773,
    longitude: 153.046992,
    markerColor: '#2baee9'
  }
};

export default ListingGallery;
window.__Components.ListingGallery = ListingGallery;
